import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  message,
  Table,
  Spin,
  Tabs,
  Tag,
  Descriptions,
  Progress,
  Alert,
  Statistic,
  Empty,
  Space,
  Typography,
} from 'antd';
import { Pie, Column, Radar } from '@ant-design/plots';
import {
  FileTextOutlined,
  TrophyOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  RiseOutlined,
  FallOutlined,
  SafetyOutlined,
  LineChartOutlined,
  DashboardOutlined,
  BarChartOutlined,
  IdcardOutlined,
  TagOutlined,
  UserOutlined,
  MailOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const { TabPane } = Tabs;
const { Text } = Typography;

// ✅ SAFE HELPERS — tüm .toFixed() çağrıları buradan geçer
const safeNum = (value) => {
  const n = parseFloat(value);
  return isNaN(n) ? 0 : n;
};
const safeFixed = (value, digits = 1) => safeNum(value).toFixed(digits);

const SupplierPerformance = () => {
  const { t } = useTranslation();

  const [supplierData, setSupplierData] = useState(null);
  const [latestPerformance, setLatestPerformance] = useState(null);
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [riskBreakdown, setRiskBreakdown] = useState([]);
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('urlToken');

  useEffect(() => {
    if (!token) {
      message.error(t('user_info_missing'));
      return;
    }

    fetchAllData();
  }, [token, apiUrl, t]);

  const fetchAllData = async () => {
    setLoading(true);

    try {
      const performanceResponse = await axios.get(
        `${apiUrl}/api/SupplierPerformance/my-performance`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('📊 Performance Data:', performanceResponse.data);

      setSupplierData(performanceResponse.data.supplier);
      setLatestPerformance(performanceResponse.data.latestPerformance);
      setPerformanceHistory(performanceResponse.data.performanceHistory || []);

      try {
        const riskResponse = await axios.get(
          `${apiUrl}/api/SupplierPerformance/my-risks`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('🔥 Risk Data:', riskResponse.data);
        setRiskBreakdown(riskResponse.data.riskAssessments || []);
      } catch (riskError) {
        console.warn('Risk data fetch error:', riskError);
        setRiskBreakdown([]);
      }
    } catch (error) {
      console.error('Data fetch error:', error);
      message.error(t('data_fetch_error'));
    } finally {
      setLoading(false);
    }
  };

  // ✅ performanceScore — her koşulda güvenli number
  const performanceScore = safeNum(
    latestPerformance?.totalScore ?? supplierData?.supplierPerformance ?? 0
  );
  const supplierClass = latestPerformance?.supplierClass || '-';

  const getPerformanceColor = (score) => {
    const s = safeNum(score);
    if (s >= 70) return '#52c41a';
    if (s >= 50) return '#faad14';
    return '#ff4d4f';
  };

  const getRiskColor = (level) => {
    if (level === 'Low') return 'green';
    if (level === 'Medium') return 'orange';
    return 'red';
  };

  const getRiskIcon = (level) => {
    if (level === 'Low') return <CheckCircleOutlined />;
    if (level === 'Medium') return <InfoCircleOutlined />;
    return <WarningOutlined />;
  };

  // ✅ Pie Chart Config
  const pieConfig = {
    data: [
      { type: t('achieved'), value: performanceScore },
      { type: t('remaining'), value: safeNum(100 - performanceScore) },
    ],
    angleField: 'value',
    colorField: 'type',
    radius: 0.9,
    innerRadius: 0.64,
    legend: false,
    statistic: {
      title: {
        offsetY: -8,
        content: t('score'),
        style: { fontSize: '13px', color: '#8c8c8c' },
      },
      content: {
        offsetY: 4,
        style: {
          fontSize: '28px',
          fontWeight: '700',
          color: getPerformanceColor(performanceScore),
        },
        // ✅ safeFixed
        content: safeFixed(performanceScore, 1),
      },
    },
    color: ({ type }) => {
      return type === t('achieved')
        ? getPerformanceColor(performanceScore)
        : '#f0f0f0';
    },
    label: false,
  };

  // ✅ Column Chart Config
  const columnConfig = {
    // ✅ score her zaman number olarak map'le
    data: performanceHistory.map((item) => ({
      period: item.period,
      score: safeNum(item.totalScore),
      class: item.supplierClass,
    })),
    xField: 'period',
    yField: 'score',
    xAxis: {
      label: {
        autoRotate: true,
        autoHide: false,
        style: {
          fontSize: 12,
        },
      },
    },
    yAxis: {
      min: 0,
      max: 100,
    },
    meta: {
      score: {
        alias: t('score'),
        min: 0,
        max: 100,
      },
      period: {
        alias: t('period'),
      },
    },
    color: ({ score }) => getPerformanceColor(score),
    tooltip: {
      formatter: (datum) => {
        return {
          name: t('score'),
          // ✅ safeFixed
          value: `${safeFixed(datum.score, 1)} (${datum.class || '-'})`,
        };
      },
    },
  };

  // ✅ Radar Chart Config
  const latestRisk = riskBreakdown.length > 0 ? riskBreakdown[0] : null;
  const radarData = latestRisk
    ? [
        { metric: 'Product Quantity', value: safeNum(latestRisk.risks.productQuantityRisk.value) },
        { metric: 'PPM',              value: safeNum(latestRisk.risks.ppmRisk.value) },
        { metric: 'Audit',            value: safeNum(latestRisk.risks.auditRisk.value) },
        { metric: 'Product Safety',   value: safeNum(latestRisk.risks.productSafetyRisk.value) },
        { metric: 'eSPICE',           value: safeNum(latestRisk.risks.eSPICEDeviationsRisk.value) },
        { metric: 'Projects',         value: safeNum(latestRisk.risks.developedProjectsRisk.value) },
        { metric: 'Maturity',         value: safeNum(latestRisk.risks.manufacturingMaturityRisk.value) },
        { metric: 'Shipment Logistics', value: safeNum(latestRisk.risks.kpiRisk.value) },
        { metric: 'CAPA',             value: safeNum(latestRisk.risks.capaRisk.value) },
      ]
    : [];

  const radarConfig = {
    data: radarData,
    xField: 'metric',
    yField: 'value',
    meta: {
      value: {
        alias: 'Risk Level',
        min: 0,
        max: 9,
      },
    },
    xAxis: {
      line: null,
      tickLine: null,
      grid: {
        line: {
          style: {
            lineDash: null,
          },
        },
      },
    },
    yAxis: {
      line: null,
      tickLine: null,
      grid: {
        line: {
          type: 'line',
          style: {
            lineDash: null,
          },
        },
      },
    },
    point: {
      size: 3,
    },
    area: {
      style: {
        fillOpacity: 0.2,
      },
    },
  };

  // ✅ Risk Table Data
  const getRiskTableData = () => {
    if (!latestRisk) return [];

    const risks = latestRisk.risks;
    return [
      {
        key: 'productQuantity',
        name: 'Product Quantity Risk',
        value: safeNum(risks.productQuantityRisk.value),
        level: risks.productQuantityRisk.level,
        description: risks.productQuantityRisk.description,
      },
      {
        key: 'ppm',
        name: 'PPM Risk',
        value: safeNum(risks.ppmRisk.value),
        level: risks.ppmRisk.level,
        description: `${risks.ppmRisk.description} (PPM: ${safeFixed(risks.ppmRisk.ppm, 2)})`,
      },
      {
        key: 'audit',
        name: 'Audit Risk',
        value: safeNum(risks.auditRisk.value),
        level: risks.auditRisk.level,
        description: risks.auditRisk.description,
      },
      {
        key: 'productSafety',
        name: 'Product Safety Risk',
        value: safeNum(risks.productSafetyRisk.value),
        level: risks.productSafetyRisk.level,
        description: risks.productSafetyRisk.description,
      },
      {
        key: 'eSPICE',
        name: 'eSPICE Deviations Risk',
        value: safeNum(risks.eSPICEDeviationsRisk.value),
        level: risks.eSPICEDeviationsRisk.level,
        description: risks.eSPICEDeviationsRisk.description,
      },
      {
        key: 'projects',
        name: 'Developed Projects Risk',
        value: safeNum(risks.developedProjectsRisk.value),
        level: risks.developedProjectsRisk.level,
        description: risks.developedProjectsRisk.description,
      },
      {
        key: 'maturity',
        name: 'Manufacturing Maturity Risk',
        value: safeNum(risks.manufacturingMaturityRisk.value),
        level: risks.manufacturingMaturityRisk.level,
        description: risks.manufacturingMaturityRisk.description,
      },
      {
        key: 'kpi',
        name: 'Shipment Logistics Risk',
        value: safeNum(risks.kpiRisk.value),
        level: risks.kpiRisk.level,
        description: risks.kpiRisk.description,
      },
      {
        key: 'capa',
        name: 'CAPA Risk',
        value: safeNum(risks.capaRisk.value),
        level: risks.capaRisk.level,
        description: risks.capaRisk.description,
      },
    ];
  };

  const riskColumns = [
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('risk_type')}</span>,
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text) => <Text style={{ fontSize: '13px', color: '#262626' }}>{text}</Text>,
    },
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('risk_value')}</span>,
      dataIndex: 'value',
      key: 'value',
      width: 100,
      render: (value) => <strong style={{ fontSize: '13px' }}>{value}</strong>,
    },
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('risk_level')}</span>,
      dataIndex: 'level',
      key: 'level',
      width: 120,
      render: (level) => (
        <Tag color={getRiskColor(level)} icon={getRiskIcon(level)} style={{ fontSize: '12px' }}>
          {level}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('description')}</span>,
      dataIndex: 'description',
      key: 'description',
      render: (text) => <Text style={{ fontSize: '13px', color: '#595959' }}>{text}</Text>,
    },
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Spin spinning={loading} size="large">
        {/* Page Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#262626' }}>
            {t('supplier_performance')}
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#8c8c8c', fontSize: '14px' }}>
            {t('track_your_performance_and_risk_metrics')}
          </p>
        </div>

        {/* Performance Alert */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Alert
              message={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Space>
                    <TrophyOutlined style={{ fontSize: '16px' }} />
                    <Text strong style={{ fontSize: '14px' }}>
                      {/* ✅ safeFixed */}
                      {t('current_performance_score')}: {safeFixed(performanceScore, 1)}
                    </Text>
                  </Space>
                  <Tag
                    color={getPerformanceColor(performanceScore)}
                    style={{ fontSize: '14px', padding: '4px 12px', margin: 0 }}
                  >
                    {t('class')}: {supplierClass}
                  </Tag>
                </div>
              }
              type={
                performanceScore >= 70
                  ? 'success'
                  : performanceScore >= 50
                  ? 'warning'
                  : 'error'
              }
              showIcon
              style={{ borderRadius: '8px' }}
            />
          </Col>
        </Row>

        {/* Statistics Cards */}
        {latestPerformance && (
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} style={{ borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    backgroundColor: '#fff7e6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <TrophyOutlined style={{ fontSize: '24px', color: '#faad14' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      {t('total_score')}
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: '600', 
                      color: getPerformanceColor(latestPerformance.totalScore),
                    }}>
                      {/* ✅ safeFixed */}
                      {safeFixed(latestPerformance.totalScore, 1)}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} style={{ borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    backgroundColor: safeNum(latestPerformance.ppm) > 100 ? '#fff1f0' : '#f6ffed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {safeNum(latestPerformance.ppm) > 100 ? (
                      <FallOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
                    ) : (
                      <RiseOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      PPM
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: '600', 
                      color: safeNum(latestPerformance.ppm) > 100 ? '#ff4d4f' : '#52c41a',
                    }}>
                      {/* ✅ safeFixed */}
                      {safeFixed(latestPerformance.ppm, 2)}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} style={{ borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    backgroundColor: '#e6f7ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <BarChartOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      {t('audit_frequency')}
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '600', color: '#262626' }}>
                      {latestPerformance.auditFrequency}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} style={{ borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    backgroundColor: safeNum(latestPerformance.totalRiskLevel) <= 15 ? '#f6ffed' : safeNum(latestPerformance.totalRiskLevel) <= 30 ? '#fff7e6' : '#fff1f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <WarningOutlined style={{ 
                      fontSize: '24px', 
                      color: safeNum(latestPerformance.totalRiskLevel) <= 15 ? '#52c41a' : safeNum(latestPerformance.totalRiskLevel) <= 30 ? '#faad14' : '#ff4d4f',
                    }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      {t('total_risk_level')}
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: '600', 
                      color: safeNum(latestPerformance.totalRiskLevel) <= 15 ? '#52c41a' : safeNum(latestPerformance.totalRiskLevel) <= 30 ? '#faad14' : '#ff4d4f',
                    }}>
                      {latestPerformance.totalRiskLevel}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Tabs */}
        <Tabs 
          defaultActiveKey="1"
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '16px',
          }}
        >
          {/* Tab 1: Overview */}
          <TabPane 
            tab={
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                <DashboardOutlined style={{ marginRight: '6px' }} />
                {t('overview')}
              </span>
            } 
            key="1"
          >
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col xs={24} lg={12}>
                <Card
                  title={
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                      <TrophyOutlined style={{ marginRight: '8px', color: '#faad14' }} />
                      {t('performance_score')}
                    </span>
                  }
                  bordered={false}
                  style={{ borderRadius: '8px', height: '450px' }}
                  bodyStyle={{
                    height: 'calc(100% - 57px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                  }}
                >
                  {!loading && !latestPerformance ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          {t('no_performance_data_available')}
                        </Text>
                      }
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%' }}>
                      <Pie {...pieConfig} height={350} />
                    </div>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card
                  title={
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                      <LineChartOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      {t('performance_history')}
                    </span>
                  }
                  bordered={false}
                  style={{ borderRadius: '8px', height: '450px' }}
                  bodyStyle={{
                    height: 'calc(100% - 57px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                  }}
                >
                  {!loading && performanceHistory.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          {t('no_evaluation_history_available')}
                        </Text>
                      }
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%' }}>
                      <Column {...columnConfig} height={350} />
                    </div>
                  )}
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={24}>
                <Card 
                  title={
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                      <SafetyOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                      {t('company_information')}
                    </span>
                  }
                  bordered={false}
                  style={{ borderRadius: '8px' }}
                  bodyStyle={{ padding: '24px' }}
                >
                  {supplierData ? (
                    <Row gutter={[24, 20]}>
                      <Col xs={24} sm={12} md={8}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            backgroundColor: '#e6f7ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <IdcardOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                              {t('company_name')}
                            </div>
                            <div style={{ 
                              fontSize: '14px', 
                              fontWeight: '500', 
                              color: '#262626',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {supplierData.supplierName || '-'}
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col xs={24} sm={12} md={8}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            backgroundColor: '#f6ffed',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <TagOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                              {t('supplier_code')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                              {supplierData.supplierCode || '-'}
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col xs={24} sm={12} md={8}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            backgroundColor: '#fffbe6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <FileTextOutlined style={{ fontSize: '18px', color: '#faad14' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                              {t('category')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                              {supplierData.category || '-'}
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col xs={24} sm={12} md={8}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            backgroundColor: '#f9f0ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <UserOutlined style={{ fontSize: '18px', color: '#722ed1' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                              {t('contact')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                              {supplierData.businessContact || '-'}
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col xs={24} sm={12} md={8}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            backgroundColor: '#e6f7ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <MailOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                              {t('email')}
                            </div>
                            <div style={{ 
                              fontSize: '14px', 
                              fontWeight: '500', 
                              color: '#1890ff',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {supplierData.businessEmail || '-'}
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col xs={24} sm={12} md={8}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            backgroundColor: '#fff1f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <EnvironmentOutlined style={{ fontSize: '18px', color: '#ff4d4f' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                              {t('address')}
                            </div>
                            <div style={{ 
                              fontSize: '14px', 
                              fontWeight: '500', 
                              color: '#262626',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {supplierData.address || '-'}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      padding: '40px 0',
                    }}>
                      <Text type="secondary" style={{ fontSize: '13px' }}>
                        {t('loading_company_info')}
                      </Text>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          </TabPane>

          {/* Tab 2: Risk Analysis */}
          <TabPane 
            tab={
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                <WarningOutlined style={{ marginRight: '6px' }} />
                {t('risk_analysis')}
              </span>
            } 
            key="2"
          >
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col xs={24} lg={12}>
                <Card
                  title={
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                      <BarChartOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      {t('risk_radar_chart')}
                    </span>
                  }
                  bordered={false}
                  style={{ borderRadius: '8px', height: '500px' }}
                  bodyStyle={{
                    height: 'calc(100% - 57px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                  }}
                >
                  {!loading && radarData.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          {t('no_risk_data_available')}
                        </Text>
                      }
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%' }}>
                      <Radar {...radarConfig} height={400} />
                    </div>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card
                  title={
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                      <InfoCircleOutlined style={{ marginRight: '8px', color: '#faad14' }} />
                      {t('risk_summary')}
                    </span>
                  }
                  bordered={false}
                  style={{ borderRadius: '8px', height: '500px' }}
                  bodyStyle={{ 
                    height: 'calc(100% - 57px)', 
                    overflowY: 'auto',
                    padding: '20px',
                  }}
                >
                  {latestRisk ? (
                    <div>
                      <div style={{ 
                        padding: '16px', 
                        backgroundColor: '#fafafa', 
                        borderRadius: '8px',
                        marginBottom: '20px',
                      }}>
                        <Row gutter={[16, 16]}>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                              {t('period')}
                            </Text>
                            <Text strong style={{ fontSize: '14px' }}>
                              {latestRisk.period}
                            </Text>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                              {t('total_risk_score')}
                            </Text>
                            <Text strong style={{ fontSize: '14px' }}>
                              {latestRisk.totalRiskScore}
                            </Text>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                              {t('total_risk_level')}
                            </Text>
                            <Tag
                              color={getRiskColor(latestRisk.totalRiskLevel)}
                              icon={getRiskIcon(latestRisk.totalRiskLevel)}
                              style={{ marginTop: '4px' }}
                            >
                              {latestRisk.totalRiskLevel}
                            </Tag>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                              {t('calculated_at')}
                            </Text>
                            <Text strong style={{ fontSize: '14px' }}>
                              {moment(latestRisk.calculatedAt).format('DD/MM/YYYY')}
                            </Text>
                          </Col>
                        </Row>
                      </div>

                      <div>
                        <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '16px' }}>
                          {t('risk_distribution')}
                        </Text>
                        {getRiskTableData().map((risk) => (
                          <div key={risk.key} style={{ marginBottom: '16px' }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '6px',
                              }}
                            >
                              <Text style={{ fontSize: '12px', color: '#595959' }}>{risk.name}</Text>
                              <Tag color={getRiskColor(risk.level)} style={{ margin: 0 }}>
                                {risk.value}
                              </Tag>
                            </div>
                            <Progress
                              percent={(risk.value / 9) * 100}
                              strokeColor={
                                getRiskColor(risk.level) === 'green'
                                  ? '#52c41a'
                                  : getRiskColor(risk.level) === 'orange'
                                  ? '#faad14'
                                  : '#ff4d4f'
                              }
                              showInfo={false}
                              size="small"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          {t('no_risk_data_available')}
                        </Text>
                      }
                    />
                  )}
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={24}>
                <Card 
                  title={
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                      <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      {t('detailed_risk_breakdown')}
                    </span>
                  }
                  bordered={false}
                  style={{ borderRadius: '8px' }}
                  bodyStyle={{ padding: '16px' }}
                >
                  {latestRisk ? (
                    <Table
                      dataSource={getRiskTableData()}
                      columns={riskColumns}
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          {t('no_risk_data_available')}
                        </Text>
                      }
                      style={{ padding: '40px 0' }}
                    />
                  )}
                </Card>
              </Col>
            </Row>
          </TabPane>

          {/* Tab 3: Performance History */}
          <TabPane 
            tab={
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                <LineChartOutlined style={{ marginRight: '6px' }} />
                {t('performance_history')}
              </span>
            } 
            key="3"
          >
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={24}>
                <Card 
                  title={
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                      <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      {t('performance_history')}
                    </span>
                  }
                  bordered={false}
                  style={{ borderRadius: '8px' }}
                  bodyStyle={{ padding: '16px' }}
                >
                  {performanceHistory.length > 0 ? (
                    <Table
                      dataSource={performanceHistory}
                      columns={[
                        {
                          title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('period')}</span>,
                          dataIndex: 'period',
                          key: 'period',
                          sorter: (a, b) => a.period.localeCompare(b.period),
                          render: (text) => <Text style={{ fontSize: '13px' }}>{text}</Text>,
                        },
                        {
                          title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('total_score')}</span>,
                          dataIndex: 'totalScore',
                          key: 'totalScore',
                          render: (score) => (
                            <span style={{ 
                              color: getPerformanceColor(safeNum(score)), 
                              fontWeight: '600',
                              fontSize: '13px',
                            }}>
                              {/* ✅ safeFixed — Tab 3 crash noktası */}
                              {safeFixed(score, 1)}
                            </span>
                          ),
                          sorter: (a, b) => safeNum(a.totalScore) - safeNum(b.totalScore),
                        },
                        {
                          title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('class')}</span>,
                          dataIndex: 'supplierClass',
                          key: 'supplierClass',
                          render: (supplierClass) => (
                            <Tag color="blue" style={{ fontSize: '12px' }}>{supplierClass}</Tag>
                          ),
                        },
                        {
                          title: <span style={{ fontWeight: '600', fontSize: '13px' }}>PPM</span>,
                          dataIndex: 'ppm',
                          key: 'ppm',
                          // ✅ safeFixed
                          render: (ppm) => (
                            <Text style={{ fontSize: '13px' }}>
                              {ppm != null ? safeFixed(ppm, 2) : '-'}
                            </Text>
                          ),
                        },
                        {
                          title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('audit_frequency')}</span>,
                          dataIndex: 'auditFrequency',
                          key: 'auditFrequency',
                          render: (freq) => <Text style={{ fontSize: '13px' }}>{freq} {t('times_per_year')}</Text>,
                        },
                        {
                          title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('created_at')}</span>,
                          dataIndex: 'createdAt',
                          key: 'createdAt',
                          render: (date) => <Text style={{ fontSize: '13px' }}>{moment(date).format('DD/MM/YYYY')}</Text>,
                        },
                      ]}
                      rowKey="performanceDataId"
                      pagination={{ 
                        pageSize: 10,
                        showSizeChanger: false,
                        size: 'small',
                      }}
                      size="small"
                    />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          {t('no_evaluation_history_available')}
                        </Text>
                      }
                      style={{ padding: '40px 0' }}
                    />
                  )}
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Spin>
    </div>
  );
};

export default SupplierPerformance;
